import { UninitializedTrackingManager } from "./states/UninitializedTrackingManager";
import { ExecutionContext } from "./types/ExecutionContext";
import { ServiceProvider } from "./dependencies_management/ServiceProvider";
import { DefaultServiceProvider } from "./dependencies_management/DefaultServiceProvider";
import { TrackingManagerState } from "./states/TrackingManagerState";
import {
	TrackedEventData,
	GaEventData,
	InternalEventData,
} from "./types/EventsData";
import { TrackingEventsSender } from "./types/TrackingEventsSender";
import { TrackingManagerStatesTransitioner } from "./types/TrackingManagerStatesTransitioner";
import { ExecutionContextsEqualityComparer } from "./utilities/execution_contexts_comparison/ExecutionContextsEqualityComparer";

const DEFAULT_SERVICE_PROVIDER: ServiceProvider = new DefaultServiceProvider();
const DEFAULT_STATE: TrackingManagerState = new UninitializedTrackingManager();

/**
 * A singleton, an entry point for the tracking library.
 * This class should be used to submit events to Google Analytics, Bing or an internal tracking system.
 */
export class TrackingManager
	implements TrackingEventsSender, TrackingManagerStatesTransitioner
{
	private currentState: TrackingManagerState;
	private executionContext: ExecutionContext | undefined;

	private readonly contextsEqualityComparer: ExecutionContextsEqualityComparer;

	constructor(
		private readonly serviceProvider: ServiceProvider = DEFAULT_SERVICE_PROVIDER,
		initialState: TrackingManagerState = DEFAULT_STATE
	) {
		this.currentState = initialState;
		this.contextsEqualityComparer =
			serviceProvider.getExecutionContextsEqualityComparer();
	}

	/**
	 * Initializes Google Analytics, Bing and internal tracking.
	 *
	 * @param executionContext An execution context to use for tracking.
	 * @returns A promise.
	 */
	async initialize(executionContext: ExecutionContext): Promise<void> {
		if (
			this.contextsEqualityComparer.areEqual(
				executionContext,
				this.executionContext
			)
		) {
			return Promise.resolve();
		}

		this.executionContext = executionContext;
		return this.transitionToState(this.currentState);
	}

	trackSharingEvent(webuserDataId: string): void {
		this.currentState.trackSharingEvent(webuserDataId);
	}

	trackSignedEvent(webuserDataId: string): void {
		this.currentState.trackSignedEvent(webuserDataId);
	}

	submitTrackedEvent(eventData: TrackedEventData): void {
		this.currentState.submitTrackedEvent(eventData);
	}

	submitInternalEvent(eventData: InternalEventData): void {
		this.currentState.submitInternalEvent(eventData);
	}

	submitGAEvent(eventData: GaEventData): void {
		this.currentState.submitGAEvent(eventData);
	}

	trackContractSection(newSection: string, newGroup: string): void {
		this.currentState.trackContractSection(newSection, newGroup);
	}

	transitionToState(newState: TrackingManagerState): Promise<void> {
		newState.trackingManager = this;
		newState.serviceProvider = this.serviceProvider;
		newState.executionContext = this.executionContext ?? {};
		this.currentState = newState;
		return this.currentState.initialize();
	}

	private static _instance: TrackingManager;

	/**
	 * Gets the shared instance of TrackingManager.
	 *
	 * @returns The TrackingManager instance.
	 */
	static getInstance(): TrackingManager {
		this._instance ??= new TrackingManager();
		return this._instance;
	}
}
